import { Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { TriageNote } from "@/hooks/use-triage";
import { useAddNote, useDeleteNote } from "@/hooks/use-triage";

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface NotesThreadProps {
  itemId: string;
  notes: TriageNote[];
}

export function NotesThread({ itemId, notes }: NotesThreadProps) {
  const [draft, setDraft] = useState("");
  const add = useAddNote(itemId);
  const remove = useDeleteNote(itemId);

  function submit() {
    const text = draft.trim();
    if (!text) return;
    add.mutate(text, {
      onSuccess: () => setDraft(""),
    });
  }

  return (
    <section className="space-y-3">
      <h3 className="font-display text-sm font-semibold">Notes</h3>
      {notes.length > 0 ? (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="rounded-md border border-[var(--color-border)] p-3 bg-card">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  {relativeTime(n.createdAt)}
                </div>
                <button
                  type="button"
                  onClick={() => remove.mutate(n.id)}
                  className="text-muted-foreground hover:text-destructive"
                  title="Delete note"
                  disabled={remove.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap">{n.text}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">No notes yet.</p>
      )}

      <div className="flex gap-2 items-end">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note…"
          rows={2}
          className="min-h-[40px] text-sm flex-1"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <Button
          size="sm"
          onClick={submit}
          disabled={!draft.trim() || add.isPending}
          title="Add note (⌘⏎)"
        >
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
    </section>
  );
}
